import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, EnvelopeSimple, Phone, Users, Note, MapPin, Cursor, PenNib, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TYPES, TYPE_META, SECTIONS, SECTION_META, ROLE_META } from "@/lib/meta";
import { Button } from "@/components/ui/button";

const TLA_LAYERS = [
  { key: "l1", label: "L1 · Human", desc: "Human trust & conversion", color: "#00D4F5" },
  { key: "l2", label: "L2 · Search", desc: "Search visibility", color: "#10B981" },
  { key: "l3", label: "L3 · AI", desc: "AI answer presence", color: "#F97316" },
];

const ACT_ICON = { email: EnvelopeSimple, call: Phone, meeting: Users, note: Note, visit: MapPin, web_action: Cursor };

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}


export default function Dashboard() {
  const { user } = useAuth();
  const [tlaBusy, setTlaBusy] = useState(false);
  const [tlaNarrative, setTlaNarrative] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const { data: resources = [], isLoading } = useQuery({
    queryKey: ["resources"],
    queryFn: async () => (await api.get("/resources")).data,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["dashboard-activities", isAdmin],
    queryFn: async () => (await api.get(isAdmin ? "/admin/activities/recent" : "/me/activities")).data,
  });

  const counts = TYPES.reduce((acc, t) => {
    acc[t] = resources.filter((r) => r.type === t).length;
    return acc;
  }, {});

  const recent = [...resources]
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
    .slice(0, 4);

  const role = ROLE_META[user?.role] || ROLE_META.lead;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Hero */}
      <div className="relative border border-border bg-card overflow-hidden grid-lines">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary" />
        <div className="relative z-10 p-8 sm:p-12">
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-primary">Welcome back</span>
          <h1 className="font-head text-3xl sm:text-5xl tracking-tight mt-2">
            {user?.name}
          </h1>
          <p className="text-muted-foreground font-light mt-3 max-w-lg leading-relaxed">
            Everything you need for your engagement with Nettpilot - agreements, docs, manuals and plugin downloads, tailored to your access level.
          </p>
          <span className={`inline-block mt-5 px-3 py-1 border text-[11px] font-mono tracking-widest uppercase ${role.color}`}>
            {role.label} access
          </span>
        </div>
      </div>

      {/* 3LA Report Card (Identity B) */}
      {user?.tla && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="tla-report-card"
          className="relative overflow-hidden border border-[#1E293B] p-8 sm:p-10"
          style={{ background: "#0F172A" }}
        >
          <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00D4F5]">Three-Layer Approach™</span>
              <h2 className="font-head text-2xl sm:text-3xl font-light text-white mt-1">Your visibility report card</h2>
              {user.tla.note && <p className="text-sm text-white/50 font-light mt-2 max-w-lg">{user.tla.note}</p>}
            </div>
            <button
              data-testid="tla-explain-button"
              onClick={async () => {
                setTlaBusy(true); setTlaNarrative(null);
                try { const { data } = await api.post("/me/ai/tla-narrative"); setTlaNarrative(data.narrative); }
                catch (e) { toast.error(e?.response?.data?.detail || "AI insights unavailable — ask your admin to enable Claude"); }
                finally { setTlaBusy(false); }
              }}
              className="flex items-center gap-2 border border-[#00D4F5]/40 text-[#00D4F5] px-4 py-2 text-xs font-mono tracking-wide hover:bg-[#00D4F5]/10 transition-colors shrink-0 disabled:opacity-50"
              disabled={tlaBusy}
            >
              <Sparkle size={15} /> {tlaBusy ? "Analyzing…" : "Explain with AI"}
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {TLA_LAYERS.map((layer) => {
              const score = Math.max(0, Math.min(100, Number(user.tla[layer.key] ?? 0)));
              return (
                <div key={layer.key} data-testid={`tla-${layer.key}`} className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[11px] tracking-widest uppercase" style={{ color: layer.color }}>{layer.label}</span>
                    <span className="font-head text-3xl font-light text-white" data-testid={`tla-${layer.key}-score`}>{score}</span>
                  </div>
                  <div className="h-1.5 bg-white/10 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full" style={{ background: layer.color }} />
                  </div>
                  <p className="text-xs text-white/40 font-light">{layer.desc}</p>
                </div>
              );
            })}
          </div>
          {tlaNarrative && (
            <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/70 font-light whitespace-pre-wrap leading-relaxed" data-testid="tla-narrative">
              {tlaNarrative}
            </div>
          )}
        </motion.div>
      )}

      {/* Services */}
      <div>
        <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">Our services</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border">
          {SECTIONS.map((s, i) => {
            const Icon = SECTION_META[s].icon;
            return (
              <motion.div key={s} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/portal/section/${s}`} data-testid={`dash-section-${s}`} className="group flex flex-col gap-4 h-full bg-card p-6 hover:bg-secondary transition-colors">
                  <Icon size={24} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  <div>
                    <p className="font-head text-sm font-medium leading-tight">{SECTION_META[s].label}</p>
                    <p className="text-xs text-muted-foreground font-light mt-1">{SECTION_META[s].blurb}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Stat grid */}
      <div>
        <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">Your library</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {TYPES.map((t, i) => {
            const Icon = TYPE_META[t].icon;
            return (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/portal/${t}`}
                  data-testid={`stat-${t}`}
                  className="group flex flex-col justify-between h-full bg-card p-6 hover:bg-secondary transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <Icon size={24} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    <ArrowUpRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-8">
                    <p className="font-head text-4xl font-light">{isLoading ? "—" : counts[t]}</p>
                    <p className="font-mono text-[11px] tracking-widest uppercase text-muted-foreground mt-1">{TYPE_META[t].label}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Recent */}
      <div>
        <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">Recently updated</h2>
        <div className="border border-border divide-y divide-border">
          {recent.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground font-light">No resources available for your access level yet.</p>
          )}
          {recent.map((r) => {
            const Icon = TYPE_META[r.type].icon;
            return (
              <Link
                key={r.id}
                to={`/portal/${r.type}`}
                className="flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors group"
                data-testid={`recent-${r.id}`}
              >
                <Icon size={20} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground truncate font-light">{r.description}</p>
                </div>
                <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground shrink-0 hidden sm:block">
                  {TYPE_META[r.type].singular}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Activity feed */}
      <div>
        <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
          {isAdmin ? "Recent activity across contacts" : "Your recent activity"}
        </h2>
        <div className="border border-border divide-y divide-border" data-testid="activity-feed">
          {activities.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground font-light" data-testid="activity-empty">No activity logged yet.</p>
          ) : (
            activities.slice(0, 10).map((a) => {
              const AIcon = ACT_ICON[a.type] || Note;
              return (
                <div key={a.id} className="flex items-start gap-4 p-4" data-testid={`activity-${a.id}`}>
                  <div className="w-8 h-8 border border-border flex items-center justify-center shrink-0 bg-secondary/40">
                    {a.created_by === "e-signature" ? <PenNib size={16} className="text-primary" /> : <AIcon size={16} className="text-muted-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{a.subject}</p>
                    <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
                      {isAdmin && a.contact_name && <span className="font-mono text-[10px] tracking-wide text-primary">{a.contact_name}</span>}
                      {a.body && <span className="text-xs text-muted-foreground truncate font-light">{a.body}</span>}
                    </div>
                  </div>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground shrink-0">{timeAgo(a.occurred_at || a.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
